<script setup>
/* Eigene Kontoeinstellungen: Anzeigename und Profilbild.
 *
 * Die Rolle steht hier nur zur Ansicht. Sie zu aendern ist Admins
 * vorbehalten — und zwar durchgesetzt in der Datenbank, nicht hier: Selbst
 * wenn diese Oberflaeche eine Rolle mitschicken wuerde, setzt ein Ausloeser
 * in Postgres sie fuer Nicht-Admins auf den alten Wert zurueck.
 * Siehe supabase/profiles.sql, Abschnitt 4.
 */

import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import OverlayModal from './OverlayModal.vue';
import PasswordField from './PasswordField.vue';
import UserAvatar from './UserAvatar.vue';
import { useAuth, validateNewPassword } from '../composables/useAuth.js';
import { useProfile } from '../composables/useProfile.js';
import { useFileActions } from '../composables/useFileActions.js';
import { useConfirm } from '../composables/useConfirm.js';

const open = defineModel({ type: Boolean, default: false });

const { t } = useI18n();
const { user, email, displayName, role, updatePassword } = useAuth();
const { profile, tableAvailable, saveProfile, loadProfile } = useProfile();
const { upload, remove } = useFileActions();
const { confirm } = useConfirm();

const name = ref('');
const avatarPath = ref('');
const busy = ref(false);
const error = ref(null);
const saved = ref(false);
const fileInput = ref(null);

/* Passwort ändern — eigener Abschnitt, eigene Fehler-/Erfolgsanzeige, damit
   er sich nicht mit dem Speichern des Anzeigenamens vermischt. Unabhängig
   von `ready`: das betrifft supabase.auth, nicht die Tabelle `profiles`. */
const newPassword = ref('');
const confirmPassword = ref('');
const pwBusy = ref(false);
const pwError = ref(null);
const pwSaved = ref(false);

/* Ohne die Tabelle `profiles` gibt es nichts zu speichern. Das ist kein
   Fehler, sondern ein noch offener Einrichtungsschritt — deshalb ein Hinweis
   und keine Fehlermeldung. */
const ready = computed(() => tableAvailable.value !== false);
const dirty = computed(
  () => name.value.trim() !== (profile.value?.full_name || '')
);

/* Beim Oeffnen den aktuellen Stand uebernehmen, damit ein Abbruch nichts
   hinterlaesst. */
watch(open, (isOpen) => {
  if (!isOpen) return;
  name.value = profile.value?.full_name || displayName.value || '';
  avatarPath.value = profile.value?.avatar_path || '';
  error.value = null;
  saved.value = false;
  newPassword.value = '';
  confirmPassword.value = '';
  pwError.value = null;
  pwSaved.value = false;
});

async function onPickPhoto(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file || !user.value) return;

  busy.value = true;
  error.value = null;
  const previous = avatarPath.value;

  try {
    const stored = await upload(file, `user-avatars/${user.value.id}`);
    await saveProfile(user.value.id, { avatarPath: stored.path });
    avatarPath.value = stored.path;
    /* Erst nach dem erfolgreichen Speichern loeschen — sonst steht bei einem
       Fehler weder das alte noch das neue Bild zur Verfuegung. */
    if (previous) await remove({ path: previous });
  } catch (e) {
    error.value = e.message || 'uploadFailed';
  } finally {
    busy.value = false;
  }
}

async function removePhoto() {
  if (!avatarPath.value || !user.value) return;
  const ok = await confirm({
    title: t('confirm.removeAvatarTitle'),
    message: t('confirm.removeAvatar'),
    confirmLabel: t('common.delete'),
  });
  if (!ok) return;

  busy.value = true;
  error.value = null;
  const previous = avatarPath.value;
  try {
    await saveProfile(user.value.id, { avatarPath: null });
    avatarPath.value = '';
    await remove({ path: previous });
  } catch (e) {
    error.value = e.message || 'saveFailed';
  } finally {
    busy.value = false;
  }
}

async function submit() {
  if (!user.value || !dirty.value) return;
  busy.value = true;
  error.value = null;
  saved.value = false;
  try {
    await saveProfile(user.value.id, { fullName: name.value.trim() || null });
    /* Neu holen, damit Kopfleiste und Dialog denselben Stand zeigen. */
    await loadProfile(user.value.id, { force: true });
    saved.value = true;
  } catch (e) {
    error.value = e.message || 'saveFailed';
  } finally {
    busy.value = false;
  }
}

async function submitPassword() {
  pwError.value = validateNewPassword(newPassword.value, confirmPassword.value);
  if (pwError.value) return;

  pwBusy.value = true;
  pwSaved.value = false;
  try {
    await updatePassword(newPassword.value);
    newPassword.value = '';
    confirmPassword.value = '';
    pwSaved.value = true;
  } catch (e) {
    pwError.value = `errors.${e.message}`;
  } finally {
    pwBusy.value = false;
  }
}
</script>

<template>
  <OverlayModal v-if="open" :title="$t('profile.title')" :width="460" @close="open = false">
    <div class="pf-photo">
      <button
        class="pf-avatar"
        :disabled="busy || !ready"
        :title="$t('profile.changePhoto')"
        @click="fileInput.click()"
      >
        <UserAvatar :name="displayName" :path="avatarPath" :size="84" />
        <span class="edit-badge" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
            <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16z" stroke-linejoin="round" />
          </svg>
        </span>
      </button>
      <input ref="fileInput" type="file" accept="image/*" hidden @change="onPickPhoto" />

      <button
        v-if="avatarPath"
        class="btn ghost small"
        :disabled="busy"
        @click="removePhoto"
      >
        {{ $t('profile.removePhoto') }}
      </button>
    </div>

    <!-- edit-grid single: dieselbe Feldgestaltung wie in der Spielerakte,
         nur einspaltig — siehe styles/main.css. -->
    <div class="pf-fields edit-grid single">
      <div class="field">
        <label for="pf-name">{{ $t('profile.displayName') }}</label>
        <input
          id="pf-name"
          v-model="name"
          type="text"
          :disabled="busy || !ready"
          :placeholder="$t('profile.displayNamePlaceholder')"
        />
      </div>

      <div class="field">
        <label>{{ $t('profile.email') }}</label>
        <div class="pf-static">{{ email }}</div>
      </div>

      <div class="field">
        <label>{{ $t('profile.role') }}</label>
        <div class="pf-static">
          {{ $t(`roles.${role}`) }}
          <span class="pf-note">{{ $t('profile.roleHint') }}</span>
        </div>
      </div>
    </div>

    <p v-if="!ready" class="pf-setup">{{ $t('profile.setupRequired') }}</p>
    <p v-else-if="error" class="pf-error">{{ $t(`errors.${error}`) }}</p>
    <p v-else-if="saved" class="pf-saved">{{ $t('profile.saved') }}</p>

    <div class="pf-password">
      <h3 class="pf-section-title">{{ $t('profile.changePassword') }}</h3>
      <div class="pf-fields edit-grid single">
        <div class="field">
          <label for="pf-new-password">{{ $t('auth.setPassword.newPassword') }}</label>
          <PasswordField
            id="pf-new-password"
            v-model="newPassword"
            autocomplete="new-password"
            :disabled="pwBusy"
          />
        </div>
        <div class="field">
          <label for="pf-confirm-password">{{ $t('auth.setPassword.confirmPassword') }}</label>
          <PasswordField
            id="pf-confirm-password"
            v-model="confirmPassword"
            autocomplete="new-password"
            :disabled="pwBusy"
          />
        </div>
      </div>

      <p v-if="pwError" class="pf-error">{{ $t(pwError) }}</p>
      <p v-else-if="pwSaved" class="pf-saved">{{ $t('profile.passwordSaved') }}</p>

      <button
        class="btn ghost small"
        :disabled="pwBusy || !newPassword || !confirmPassword"
        @click="submitPassword"
      >
        {{ pwBusy ? $t('common.loading') : $t('auth.setPassword.submit') }}
      </button>
    </div>

    <template #actions>
      <button class="btn ghost small" :disabled="busy" @click="open = false">
        {{ $t('common.close') }}
      </button>
      <button class="btn small" :disabled="busy || !dirty || !ready" @click="submit">
        {{ busy ? $t('common.loading') : $t('common.save') }}
      </button>
    </template>
  </OverlayModal>
</template>

<style scoped>
.pf-photo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}
.pf-avatar {
  position: relative;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
}
.pf-avatar:disabled {
  cursor: default;
  opacity: 0.6;
}
.edit-badge {
  position: absolute;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--card);
  border: 1px solid var(--border);
  color: var(--muted);
}
.pf-avatar:hover:not(:disabled) .edit-badge {
  color: var(--green);
  border-color: var(--green-dim);
}

.pf-fields .field input:disabled {
  opacity: 0.6;
}
.pf-static {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  padding: 9px 0 0;
  font-size: 0.88rem;
  color: var(--muted);
}
.pf-note {
  font-size: 0.7rem;
  color: var(--dim);
}

.pf-password {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid var(--border-soft);
}
.pf-section-title {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 12px;
}
.pf-password .btn {
  margin-top: 4px;
}

.pf-setup,
.pf-error,
.pf-saved {
  margin-top: 16px;
  font-size: 0.78rem;
  line-height: 1.6;
}
.pf-setup {
  color: var(--amber);
}
.pf-error {
  color: var(--red);
}
.pf-saved {
  color: var(--green);
}
.btn:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
